import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import bankService from "../../../services/bank.service";
import BankRecordDetailModal from "../../../components/bank/BankRecordDetailModal";
import VehicleChassisSearchBar from "../../repo/vehicles/VehicleChassisSearchBar";
import {
  resolveActiveSearch,
  sanitizeVehicleSuffixInput,
  sanitizeChassisSuffixInput,
  getFindVehiclesValidationMessage,
  sortFindVehicleItems,
  splitResultsByColumn,
} from "../../repo/vehicles/findVehiclesHelpers";
import PaginationBar from "../../../components/common/PaginationBar";
import { PAGE_LIMIT } from "../../../constants/pagination";
import { readPagination } from "../../../utils/listPagination";
import { normalizeVehicleNumber } from "../../../utils/vehicleNumberUtils";
import "../../../styles/vehicles.css";
import "../../../styles/bankRecordDetail.css";

const FETCH_DEBOUNCE_MS = 280;

function cardPrimaryLabel(item, mode) {
  if (mode === "chassis") {
    return String(item.chassisNumber || "").trim() || "—";
  }
  const vn = item.vehicleNumber;
  if (!vn) return "—";
  return normalizeVehicleNumber(vn);
}

export default function BankVehicleSearch() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlQ = searchParams.get("q") || "";
  const urlType = searchParams.get("type") === "chassis" ? "chassis" : "vehicle";

  const [draftVehicle, setDraftVehicle] = useState("");
  const [draftChassis, setDraftChassis] = useState("");
  const [lastInteraction, setLastInteraction] = useState("vehicle");
  const [formError, setFormError] = useState("");

  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    const resolved = resolveActiveSearch(draftVehicle, draftChassis, lastInteraction);
    setFormError(
      resolved
        ? ""
        : getFindVehiclesValidationMessage(draftVehicle, draftChassis, lastInteraction)
    );
    if (!resolved) {
      return undefined;
    }
    const timer = setTimeout(() => {
      navigate(
        `/bank/vehicle-search?q=${encodeURIComponent(resolved.normalized)}&type=${resolved.mode}`,
        { replace: true }
      );
      setDraftVehicle("");
      setDraftChassis("");
      setFormError("");
    }, 320);
    return () => clearTimeout(timer);
  }, [draftVehicle, draftChassis, lastInteraction, navigate]);

  useEffect(() => {
    setPage(1);
  }, [urlQ, urlType]);

  useEffect(() => {
    if (!urlQ) {
      setRecords([]);
      setLoading(false);
      setError("");
      return undefined;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const res = await bankService.getRecords({
          page,
          limit: PAGE_LIMIT,
          search: urlQ,
          searchType: urlType,
        });
        setRecords(res?.data?.data?.records || []);
        setPagination(readPagination(res?.data));
      } catch (err) {
        setError(err?.response?.data?.message || "Could not search vehicles.");
        setRecords([]);
      } finally {
        setLoading(false);
      }
    }, FETCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [urlQ, urlType, page]);

  const { left, right } = useMemo(
    () => splitResultsByColumn(sortFindVehicleItems(records, urlType)),
    [records, urlType]
  );

  const renderCell = (item, index) => (
    <button
      type="button"
      key={`${item._id || item.id || "row"}-${index}`}
      className="fv-excel-grid__cell"
      onClick={() => setDetailId(item._id)}
      title="View details"
    >
      <div className="fv-excel-grid__cell-main">
        <span className="fv-excel-grid__cell-text">{cardPrimaryLabel(item, urlType)}</span>
        <span className="fv-excel-grid__cell-chev" aria-hidden>
          ›
        </span>
      </div>
      {item.borrowerName ? (
        <div className="fv-excel-grid__cell-sub">{item.borrowerName}</div>
      ) : null}
    </button>
  );

  return (
    <div className="page find-vehicles-page fv-find-results-layout">
      <div className="fv-search-card fv-mobile-search-shell">
        <VehicleChassisSearchBar
          draftVehicle={draftVehicle}
          draftChassis={draftChassis}
          onVehicleChange={(value) => {
            setLastInteraction("vehicle");
            setDraftVehicle(sanitizeVehicleSuffixInput(value));
          }}
          onChassisChange={(value) => {
            setLastInteraction("chassis");
            setDraftChassis(sanitizeChassisSuffixInput(value));
          }}
          onVehicleFocus={() => setLastInteraction("vehicle")}
          onChassisFocus={() => setLastInteraction("chassis")}
          onClearVehicle={() => setDraftVehicle("")}
          onClearChassis={() => setDraftChassis("")}
        />
      </div>

      {formError ? <p className="fv-error fv-results-minimal-error">{formError}</p> : null}
      {error ? <p className="fv-error fv-results-minimal-error">{error}</p> : null}

      <div className="fv-results-minimal-body">
        {loading && records.length === 0 ? <div className="fv-empty-box">Searching…</div> : null}

        {!loading && urlQ && records.length === 0 && !error ? (
          <div className="fv-empty-box">
            No match for <strong>{urlQ}</strong> ({urlType === "chassis" ? "chassis" : "vehicle"}).
          </div>
        ) : null}

        {records.length > 0 ? (
          <>
            <div className="fv-results-total-row">
              <span className="fv-results-count">
                Total records: {(pagination.total || records.length).toLocaleString("en-IN")}
              </span>
            </div>
            <div className="fv-excel-grid" aria-label="Search results">
              <div className="fv-excel-grid__col fv-excel-grid__col--left">
                {left.map((item, index) => renderCell(item, index))}
              </div>
              <div className="fv-excel-grid__col fv-excel-grid__col--right">
                {right.map((item, index) => renderCell(item, index))}
              </div>
            </div>
            <PaginationBar
              page={pagination.page || page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              loading={loading}
              onPageChange={setPage}
            />
          </>
        ) : null}
      </div>

      {detailId ? (
        <BankRecordDetailModal
          recordId={detailId}
          fetchMode="bank"
          onClose={() => setDetailId(null)}
        />
      ) : null}
    </div>
  );
}
